/**
 * Reveals newsletter heading word-by-word from low to full opacity on scroll.
 */
class NewsletterScrollReveal extends HTMLElement {
  /** @type {HTMLElement[]} */
  #words = [];
  /** @type {number} */
  #baseOpacity = 0.2;
  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    const base = Number(this.dataset.baseOpacity);
    this.#baseOpacity = Number.isFinite(base) ? base : 0.2;

    this.#prepareWords();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#setAllOpacity(1);
      return;
    }

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #prepareWords() {
    const textEl = this.querySelector('[data-reveal-text]');
    if (!(textEl instanceof HTMLElement)) return;

    const raw = textEl.textContent?.trim() ?? '';
    if (!raw) return;

    const parts = raw.split(/(\s+)/);
    textEl.replaceChildren();
    textEl.dataset.revealReady = 'true';

    this.#words = [];

    for (const part of parts) {
      if (part === '') continue;

      if (/^\s+$/.test(part)) {
        textEl.appendChild(document.createTextNode(part));
        continue;
      }

      const span = document.createElement('span');
      span.className = 'newsletter-scroll-reveal__word';
      span.dataset.revealWord = '';
      span.textContent = part;
      span.style.opacity = String(this.#baseOpacity);
      textEl.appendChild(span);
      this.#words.push(span);
    }
  }

  #onScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update() {
    if (this.#words.length === 0) return;

    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight || 1;
    const start = viewport * 0.9;
    const end = viewport * 0.2;
    const range = Math.max(1, start - end);
    const progress = Math.min(1, Math.max(0, (start - rect.top) / range));
    const scaled = progress * this.#words.length;

    for (let i = 0; i < this.#words.length; i++) {
      const wordProgress = scaled - i;
      const opacity = Math.min(1, Math.max(this.#baseOpacity, wordProgress));
      this.#words[i].style.opacity = String(opacity);
    }
  }

  /**
   * @param {number} opacity
   */
  #setAllOpacity(opacity) {
    for (const word of this.#words) {
      word.style.opacity = String(opacity);
    }
  }
}

if (!customElements.get('newsletter-scroll-reveal')) {
  customElements.define('newsletter-scroll-reveal', NewsletterScrollReveal);
}
